// Shared per-player season-stats rollup.
//
// This is the SINGLE source of truth for summing a season's per-game stats into
// per-player batting/pitching totals. It was extracted verbatim from StatsView so
// the Stats tab and Season Wrapped can never disagree on a player's numbers.
// Pure + framework-free: pass games + players, get the finalized `stats` map back.
//
// The shape returned per player matches what StatsView builds (and what its
// `coachsEyePayload` / Wrapped both consume): { player, gp, bench, positions,
// batting{...}, pitching{...} } with rates finalized and the GameChanger
// season-snapshot override applied (current season only).

import { calculateFIP, calculateBABIP, calculateDSS } from './dss-engine';
import { gcInningsToDecimal, gameLengthInnings, fieldTimeRecordFor, type FieldTimeSource } from './gc-innings';

/**
 * GC CSVs report rates inconsistently: "46.51" for 46.51% in some columns,
 * "0.4651" in others. Normalize both to a 0–1 fraction. Values > 1 are
 * treated as percentages. (Exported so import/display code shares one rule.)
 */
export const normalizeRate = (val: any): number | null => {
  if (val == null || val === '') return null;
  const n = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(n)) return null;
  return n > 1 ? n / 100 : n;
};

/**
 * Convert baseball-notation innings (4.2 = 4⅔ innings) to true innings (4.667).
 *
 * Kept as a named export for existing callers, but delegating to the single
 * implementation in ./gc-innings. This rule used to live here and be applied ONLY
 * to pitching IP, while the fielding-position columns of the same CSV were read
 * with parseFloat + Math.round — so the file knew GC's notation and the field-time
 * heatmap still got it wrong. One implementation, one place, so they cannot drift
 * apart again.
 */
export const ipToInnings = gcInningsToDecimal;

const parseNum = (v: any): number | null => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return isNaN(n) ? null : n;
};

/**
 * Sum a counting stat only when the game actually reported it. Seeds lazily so
 * a stat absent from every import stays `undefined` (renders as "—"), never a
 * fake 0. (Story stats were added to the parser 2026-07; older imports lack them.)
 */
const addCount = (bucket: any, key: string, val: any) => {
  const n = parseNum(val);
  if (n == null) return;
  bucket[key] = (bucket[key] ?? 0) + n;
};

/** Accumulate a weighted rate into hidden _sum_/_w_ keys (finalized + cleaned later). */
const addWeighted = (bucket: any, key: string, value: number | null, weight: number) => {
  if (value == null || !(weight > 0)) return;
  bucket[`_sum_${key}`] = (bucket[`_sum_${key}`] ?? 0) + value * weight;
  bucket[`_w_${key}`] = (bucket[`_w_${key}`] ?? 0) + weight;
};

/** Finalize every _sum_/_w_ pair into its plain key, then remove the scaffolding. */
const finalizeWeighted = (bucket: any) => {
  Object.keys(bucket).forEach(k => {
    if (!k.startsWith('_sum_')) return;
    const key = k.slice(5);
    const w = bucket[`_w_${key}`];
    if (w > 0 && bucket[key] == null) bucket[key] = bucket[k] / w;
    delete bucket[k];
    delete bucket[`_w_${key}`];
  });
};

export interface SeasonStatsResult {
  /** Keyed by player id. */
  stats: Record<string, any>;
  /** Each pitcher's most recent GC-imported outing (for the "Last" column). */
  lastGCOuting: Record<string, { date: string; opponent: string; pitches: number }>;
  /** Count of completed games that contributed inning-log / field-time data. */
  totalGamesWithLogs: number;
}

/**
 * Build finalized per-player season totals from a season's games.
 *
 * @param players   roster (each needs at least { id, name })
 * @param games     the season's games (already filtered to the season by caller)
 * @param opts.isHistoryMode  when true, skip the live `seasonSnapshot` override
 *                            (a past season reconstructs fully from its games)
 * @param opts.fieldTimeSource the team's "Field positions come from" setting
 *                            (fieldTimeSourceOf(team)); defaults to GameChanger
 * @param opts.defaultInnings the team's Default Innings, the game length used
 *                            for a game with no GameChanger import
 */
export function buildSeasonStats(
  players: any[],
  games: any[],
  opts: { isHistoryMode?: boolean; fieldTimeSource?: FieldTimeSource; defaultInnings?: number } = {},
): SeasonStatsResult {
  const isHistoryMode = !!opts.isHistoryMode;
  const fieldTimeSource = opts.fieldTimeSource ?? 'gamechanger';
  const defaultInnings = opts.defaultInnings;

  const lastGCOuting: Record<string, { date: string; opponent: string; pitches: number }> = {};

  const stats: Record<string, any> = {};
  players.forEach(p => {
    stats[p.id] = {
      player: p,
      gp: 0,
      bench: 0,
      positions: { P: 0, C: 0, '1B': 0, '2B': 0, SS: 0, '3B': 0, LF: 0, CF: 0, RF: 0, LCF: 0, RCF: 0 },
      // Aggregated Batting
      batting: {
        gp: 0, pa: 0, ab: 0, h: 0, bb: 0, so: 0, rbi: 0, r: 0, sb: 0,
        // We will compute rates at the end
        total_avg_weight: 0, total_obp_weight: 0, total_ops_weight: 0,
        sum_avg: 0, sum_obp: 0, sum_ops: 0,
      },
      // Aggregated Pitching
      pitching: {
        gp: 0, ip: 0, pitches: 0, so: 0, bb: 0, h: 0, w: 0, l: 0,
        sum_era: 0, sum_whip: 0,
        // Pitching Intel Metrics
        sum_s_pct: 0, total_s_weight: 0,
        sum_fps_pct: 0, total_fps_weight: 0,
        sum_p_bf: 0, total_p_bf_weight: 0,
        // FIP / BABIP / DSS inputs
        hr: 0, hbp: 0, bf: 0, er: 0,
        // Run game against us. GC's pitching section reports both:
        // "SB = Stolen bases allowed", "CS = Runners caught stealing".
        sbAllowed: 0, csAllowed: 0,
      },
    };
  });

  let totalGamesWithLogs = 0;

  games.forEach(game => {
    // 1. Process FTE / Inning Logs — completed games only, matching what the lineup engine tracks
    //
    // Source precedence is per GAME and follows the team's setting (see
    // fieldTimeRecordFor). By default a GameChanger import supersedes the live
    // game engine's inningLogs: GC counts every defensive inning of a finished
    // game, while a live log holds only what the coach had time to tap in. A
    // team on "Dugout live games" flips that for any game whose log covers the
    // whole game, because their GC scorekeeper doesn't keep up with defensive
    // changes.
    //
    // With no import, a live log covering under half the game ('partial') is a
    // stub rather than a record. Counting it would credit every OTHER player
    // with a full game's bench time measured against a couple of logged
    // innings, and silently drop the innings of anyone absent from the
    // fragment. Skip the game until an import gives it a real record. Mirrors
    // the "Not recorded" rule in field-time-report.ts so the Stats matrix and
    // the printable report cannot disagree.
    const record = fieldTimeRecordFor(game, fieldTimeSource, defaultInnings);
    if (record === 'partial') return;

    if (game.status === 'completed' && record === 'live') {
      totalGamesWithLogs++;
      const playersInGame = new Set<string>();

      Object.values(game.inningLogs).forEach((inningLineup: any) => {
        inningLineup.forEach((p: any) => {
          if (!stats[p.id]) return;
          playersInGame.add(p.id);
          if (p.position === 'BENCH' || p.position === 'EH') {
            stats[p.id].bench++;
          } else if (stats[p.id].positions[p.position] !== undefined) {
            stats[p.id].positions[p.position]++;
          }
        });
      });

      playersInGame.forEach(pid => {
        if (stats[pid]) stats[pid].gp++;
      });
    } else if (game.status === 'completed' && record === 'gc') {
      // GC-imported position innings (per-position totals for the game)
      totalGamesWithLogs++;

      // Total innings for this game, to calculate bench time. See
      // gameLengthInnings for why the stored inferredInnings alone falls short.
      const gameInnings = gameLengthInnings(game, defaultInnings);

      Object.entries(game.gcFieldTime).forEach(([pid, positions]: [string, any]) => {
        if (!stats[pid]) return;
        stats[pid].gp++;

        let totalInningsInField = 0;
        Object.entries(positions).forEach(([pos, innings]: [string, any]) => {
          // GC writes innings as OUTS ("2.1" is 2 1/3), so parseFloat + Math.round
          // both understated the value and DELETED any stint under half an inning.
          // Accumulate the true fraction and round once at display instead.
          const count = gcInningsToDecimal(innings);
          if (count <= 0) return;
          totalInningsInField += count;

          if (pos === 'SF') {
            // Short field (4th outfielder) — count as CF for display
            stats[pid].positions['CF'] = (stats[pid].positions['CF'] || 0) + count;
          } else if (stats[pid].positions[pos] !== undefined) {
            stats[pid].positions[pos] += count;
          }
        });

        // Bench = Total Game Innings - Total Field Innings
        const benchInnings = Math.max(0, gameInnings - totalInningsInField);
        stats[pid].bench += benchInnings;
      });
    }

    // 2. Process Game Stats (CSV Imports)
    if (game.stats) {
      // The onboarding container is one doc holding each player's whole-season
      // line, so it stands for the CSV's GP games, not one.
      const isSeasonLine = game.source === 'onboardingImport';
      Object.entries(game.stats).forEach(([pid, s]: [string, any]) => {
        if (!stats[pid]) return;
        const pStat = stats[pid];
        // Pitching counts live under their own keys (soAllowed, bbAllowed,
        // hAllowed) because `so`, `bb` and `h` are the BATTING line. Only fall
        // back to the bare key on legacy docs that never had the pitching key:
        // `||` here used to swap in a pitcher's batting strikeouts whenever he
        // struck out nobody, and hits allowed always read batting hits.
        const pitchingCount = (key: string, legacy: string) =>
          (key in s ? s[key] : s[legacy]) || 0;

        // Track most recent GC pitching outing for "Last" column. The onboarding
        // container holds season totals dated at signup, not an outing.
        if ((s.ip > 0 || s.pitches > 0) && game.date && game.source !== 'onboardingImport') {
          const prev = lastGCOuting[pid];
          if (!prev || game.date > prev.date) {
            lastGCOuting[pid] = {
              date: game.date,
              opponent: game.opponent || '?',
              pitches: parseInt(s.pitches) || 0,
            };
          }
        }

        // Batting Aggregation
        if (s.pa > 0 || s.ab > 0) {
          pStat.batting.gp += isSeasonLine ? (Number(s.gp) || 1) : 1;
          pStat.batting.pa += (s.pa || 0);
          pStat.batting.ab += (s.ab || 0);
          pStat.batting.h += (s.h || 0);
          pStat.batting.bb += (s.bb || 0);
          pStat.batting.so += (s.so || 0);
          pStat.batting.rbi += (s.rbi || 0);
          pStat.batting.r += (s.r || 0);
          pStat.batting.sb += (s.sb || 0);

          // Story counting stats (2026-07 parser additions) — lazily seeded so
          // pre-extension imports leave them undefined (→ "—"), never fake 0.
          addCount(pStat.batting, 'hbp', s.hbp);
          addCount(pStat.batting, 'xbh', s.xbh);
          addCount(pStat.batting, 'qab', s.qab);
          addCount(pStat.batting, 'hhb', s.hhb);
          addCount(pStat.batting, 'twoOutRbi', s.twoOutRbi);
          addCount(pStat.batting, 'lob', s.lob);
          addCount(pStat.batting, 'ps', s.ps);
          addCount(pStat.batting, 'kLooking', s.kLooking);
          addCount(pStat.batting, 'twoStrikeBattle', s.twoStrikeBattle);

          // Story rates — weighted like the existing avg/obp/ops pattern.
          const abW = s.ab || 1;
          const paW = s.pa || 1;
          addWeighted(pStat.batting, 'slg', parseNum(s.slg), abW);
          addWeighted(pStat.batting, 'contactPct', normalizeRate(s.contactPct), abW);
          addWeighted(pStat.batting, 'baRisp', parseNum(s.baRisp), abW); // PA-proportional approximation; GC exports no RISP-AB base
          addWeighted(pStat.batting, 'ldPct', normalizeRate(s.ldPct), abW);
          addWeighted(pStat.batting, 'fbPct', normalizeRate(s.fbPct), abW);
          addWeighted(pStat.batting, 'gbPct', normalizeRate(s.gbPct), abW);
          addWeighted(pStat.batting, 'babipBat', parseNum(s.babipBat), abW);
          // Fallbacks only — finalize prefers count ÷ PA when counts exist.
          addWeighted(pStat.batting, 'qabPct', normalizeRate(s.qabPct), paW);
          addWeighted(pStat.batting, 'psPa', parseNum(s.psPa), paW);
          addWeighted(pStat.batting, 'battlePct', normalizeRate(s.battlePct), paW);

          // For rates, we'll do a simple weighted average by PA/AB for now
          // until we have full counting stats for 1B/2B/3B/HR/HBP/SF
          if (s.avg != null) {
            pStat.batting.sum_avg += (s.avg * (s.ab || 1));
            pStat.batting.total_avg_weight += (s.ab || 1);
          }
          if (s.obp != null) {
            pStat.batting.sum_obp += (s.obp * (s.pa || 1));
            pStat.batting.total_obp_weight += (s.pa || 1);
          }
          if (s.ops != null) {
            pStat.batting.sum_ops += (s.ops * (s.pa || 1));
            pStat.batting.total_ops_weight += (s.pa || 1);
          }
        }

        // Pitching Aggregation
        if (s.ip > 0 || (s.pitches > 0)) {
          if (!isSeasonLine) pStat.pitching.gp++;
          else if (s.gpPitching != null) pStat.pitching.gp += Number(s.gpPitching) || 0;
          // Season lines imported before the parser kept pitching GP: the
          // true count is unknown, so show "—" rather than a wrong 1.
          else pStat.pitching.gpUnknown = true;
          pStat.pitching.ip += (s.ip || 0);
          pStat.pitching.pitches += (s.pitches || 0);
          pStat.pitching.so += pitchingCount('soAllowed', 'so');
          pStat.pitching.bb += pitchingCount('bbAllowed', 'bb');
          pStat.pitching.h += pitchingCount('hAllowed', 'h');
          pStat.pitching.w += (s.w || 0);
          pStat.pitching.l += (s.l || 0);

          if (s.era != null) pStat.pitching.sum_era += (s.era * (s.ip || 1));
          if (s.whip != null) pStat.pitching.sum_whip += (s.whip * (s.ip || 1));

          // Pitching Intelligence: Strikes, FPS, P/BF
          // (normalizeRate is the module-level export above)
          const gameSPct = normalizeRate(s.sPct);
          const gameFPSPct = normalizeRate(s.fpsPct);
          const gamePitches = parseInt(s.pitches) || 0;
          const gameIP = parseFloat(s.ip) || 0;

          if (gameSPct != null) {
            const weight = gamePitches || 1;
            pStat.pitching.sum_s_pct += (gameSPct * weight);
            pStat.pitching.total_s_weight += weight;
          }
          if (gameFPSPct != null) {
            // FPS% should be weighted by Batters Faced (BF) if possible.
            // s.bf is often in GC exports, or we fall back to a reasonable estimate
            const weight = parseInt(s.bf) || Math.round(gameIP * 4) || 1;
            pStat.pitching.sum_fps_pct += (gameFPSPct * weight);
            pStat.pitching.total_fps_weight += weight;
          }
          if (s.p_bf != null) {
            const pbf = parseFloat(s.p_bf);
            if (!isNaN(pbf)) {
              // P/BF is a rate (Pitches per Batter Faced)
              // We weight it by total batters faced in that game
              const weight = parseInt(s.bf) || Math.round(gameIP * 4) || 1;
              pStat.pitching.sum_p_bf += (pbf * weight);
              pStat.pitching.total_p_bf_weight += weight;
            }
          }

          // FIP / BABIP inputs — counting totals
          pStat.pitching.hr  += (s.hrAllowed  || 0);
          pStat.pitching.hbp += (s.hbpAllowed || 0);
          pStat.pitching.sbAllowed += (s.sbAllowed || 0);
          pStat.pitching.csAllowed += (s.csAllowed || 0);
          pStat.pitching.er  += (s.erAllowed  || 0);
          // BF fallback: (IP × 3) + H + BB + HBP when GC doesn't export BF
          const gameBF = (s.bf != null && s.bf > 0)
            ? s.bf
            : Math.round((s.ip || 0) * 3) + (s.hAllowed || 0) + pitchingCount('bbAllowed', 'bb') + (s.hbpAllowed || 0);
          pStat.pitching.bf += gameBF;

          // True innings (GC IP is thirds notation — 0.2 = ⅔; decimal sums are
          // wrong for per-inning rates, so keep an honest total alongside).
          pStat.pitching.ipTrue = (pStat.pitching.ipTrue ?? 0) + ipToInnings(s.ip);

          // Story counting stats (lazily seeded — see batting note above)
          addCount(pStat.pitching, 'zeroBbInn', s.zeroBbInn);
          addCount(pStat.pitching, 'bbs', s.bbs);
          addCount(pStat.pitching, 'lobb', s.lobb);
          addCount(pStat.pitching, 'loo', s.loo);
          addCount(pStat.pitching, 'inn123', s.inn123);
          addCount(pStat.pitching, 'lowPitchInn', s.lowPitchInn);
          addCount(pStat.pitching, 'rAllowed', s.rAllowed); // self-inflicted-runs denominator

          // Story rates — weighted by batters faced / pitches
          addWeighted(pStat.pitching, 'fpsoPct', normalizeRate(s.fpsoPct), gameBF);
          addWeighted(pStat.pitching, 'fpswPct', normalizeRate(s.fpswPct), gameBF);
          addWeighted(pStat.pitching, 'fpshPct', normalizeRate(s.fpshPct), gameBF);
          addWeighted(pStat.pitching, 'kBf', parseNum(s.kBf), gameBF);
          addWeighted(pStat.pitching, 'weakPct', normalizeRate(s.weakPct), gameBF);
          addWeighted(pStat.pitching, 'baa', parseNum(s.baa), gameBF);
          addWeighted(pStat.pitching, 'goAo', parseNum(s.goAo), gameBF);
          addWeighted(pStat.pitching, 'smPct', normalizeRate(s.smPct), gamePitches || 1);
        }
      });
    }
  });

  // Finalize rates
  Object.values(stats).forEach((s: any) => {
    if (s.batting.total_avg_weight > 0) s.batting.avg = s.batting.sum_avg / s.batting.total_avg_weight;
    if (s.batting.total_obp_weight > 0) s.batting.obp = s.batting.sum_obp / s.batting.total_obp_weight;
    if (s.batting.total_ops_weight > 0) s.batting.ops = s.batting.sum_ops / s.batting.total_ops_weight;

    // Story rates — counts win over weighted-average fallbacks (more honest math)
    if (s.batting.qab != null && s.batting.pa > 0) s.batting.qabPct = s.batting.qab / s.batting.pa;
    if (s.batting.ps != null && s.batting.pa > 0) s.batting.psPa = s.batting.ps / s.batting.pa;
    if (s.batting.twoStrikeBattle != null && s.batting.pa > 0) s.batting.battlePct = s.batting.twoStrikeBattle / s.batting.pa;
    finalizeWeighted(s.batting);

    // Per-inning pitching rates from true innings (thirds-notation-safe)
    if (s.pitching.ipTrue > 0) {
      if (s.pitching.pitches > 0) s.pitching.pIp = s.pitching.pitches / s.pitching.ipTrue;
      s.pitching.bbInn = s.pitching.bb / s.pitching.ipTrue;
    }
    finalizeWeighted(s.pitching);
    if (s.pitching.gpUnknown) s.pitching.gp = null;
    delete s.pitching.gpUnknown;

    // S%, FPS%, P/BF don't require IP — available even when a pitcher recorded 0 outs
    if (s.pitching.total_s_weight > 0) s.pitching.sPct = s.pitching.sum_s_pct / s.pitching.total_s_weight;
    if (s.pitching.total_fps_weight > 0) s.pitching.fpsPct = s.pitching.sum_fps_pct / s.pitching.total_fps_weight;
    if (s.pitching.total_p_bf_weight > 0) s.pitching.p_bf = s.pitching.sum_p_bf / s.pitching.total_p_bf_weight;

    if (s.pitching.ip > 0) {
      s.pitching.era = s.pitching.sum_era / s.pitching.ip;
      s.pitching.whip = s.pitching.sum_whip / s.pitching.ip;

      // FIP / BABIP / DSS — only when we have the counting stats from a GC import
      const hasFIPData = s.pitching.bf > 0;
      if (hasFIPData) {
        s.pitching.fip   = calculateFIP(s.pitching.so, s.pitching.bb, s.pitching.hbp, s.pitching.hr, s.pitching.ip);
        s.pitching.babip = calculateBABIP(s.pitching.h, s.pitching.hr, s.pitching.bf, s.pitching.bb, s.pitching.hbp, s.pitching.so);
        s.pitching.dss   = s.pitching.fip != null
          ? calculateDSS(s.pitching.era, s.pitching.fip, s.pitching.babip, s.pitching.ip)
          : null;
      }
    }
  });

  // ── Season-snapshot override (anti double-count) ───────────────────────────
  // A season export states innings per POSITION but never the length of a game,
  // and bench time is meaningless without it. The busiest fielder's average
  // (their season innings / their games played) is the tightest available floor:
  // no fielder can be on the field longer than the game, and on a normal roster
  // at least one player goes close to wire-to-wire. Computed once, across the
  // roster, so every player's bench is measured against the same game length.
  let seasonInningsPerGame = 0;
  if (!isHistoryMode) {
    players.forEach((p: any) => {
      const ft = p?.seasonSnapshot?.fieldTime;
      const gp = p?.seasonSnapshot?.stats?.gp ?? 0;
      if (!ft || typeof ft !== 'object' || gp <= 0) return;
      let total = 0;
      Object.values(ft).forEach(v => { total += gcInningsToDecimal(v); });
      const perGame = total / gp;
      if (perGame > seasonInningsPerGame) seasonInningsPerGame = perGame;
    });
  }

  // For players with an imported GameChanger season snapshot, DISPLAY those
  // totals instead of the per-game sum above. This is what stops a lump-sum
  // season file from double-counting on top of game-by-game data. Per-game data
  // is untouched and still powers the charts/streaks (which read `g.stats`).
  players.forEach((p: any) => {
    // History mode: a past season's per-game data fully reconstructs its totals
    // from the season-tagged games above, so we don't apply the live snapshot
    // override (which belongs to the CURRENT season and would be wrong here).
    const snapRoot = isHistoryMode ? undefined : p?.seasonSnapshot;
    const snap = snapRoot?.stats;
    const s = stats[p.id];
    if (!s) return;

    // Field time is stored alongside the snapshot's batting/pitching totals, but
    // it is a SIBLING of `stats`, not a member of it. It has to be applied on its
    // own: a whole-season export can carry position innings for a player whose
    // batting line is empty (a defensive replacement who never came to the plate),
    // and bailing out on `!snap` above would drop that player's innings entirely.
    //
    // Same anti-double-count rule as the rest of this block: the season file is
    // authoritative, so it REPLACES the per-game sum rather than adding to it.
    const snapField = snapRoot?.fieldTime;
    if (snapField && typeof snapField === 'object') {
      Object.keys(s.positions).forEach(pos => { s.positions[pos] = 0; });
      let fieldTotal = 0;
      Object.entries(snapField).forEach(([pos, innings]: [string, any]) => {
        const count = gcInningsToDecimal(innings);
        if (count <= 0) return;
        fieldTotal += count;
        // Short field (4th outfielder) displays as CF, matching the per-game path.
        const mapped = pos === 'SF' ? 'CF' : pos;
        if (s.positions[mapped] !== undefined) s.positions[mapped] += count;
      });

      // Bench for a season file = (games played x game length) - innings in the
      // field. Innings-per-game comes from the team's own longest fielder, since
      // a season export never states game length. Without this the matrix showed
      // per-game bench totals next to season field totals, which do not belong to
      // each other.
      const gp = snap?.gp ?? 0;
      if (gp > 0 && seasonInningsPerGame > 0) {
        s.bench = Math.max(0, gp * seasonInningsPerGame - fieldTotal);
      } else {
        s.bench = 0;
      }
    }

    if (!snap) return;

    s.batting = {
      ...s.batting,
      gp:  snap.gp  ?? s.batting.gp,
      pa:  snap.pa  ?? 0,
      ab:  snap.ab  ?? 0,
      h:   snap.h   ?? 0,
      bb:  snap.bb  ?? 0,
      so:  snap.so  ?? 0,
      rbi: snap.rbi ?? 0,
      r:   snap.r   ?? 0,
      sb:  snap.sb  ?? 0,
      avg: snap.avg ?? 0,
      obp: snap.obp ?? 0,
      ops: snap.ops ?? 0,
      // Story stats: snapshot wins when it has the field, else keep the per-game
      // sum, else stay undefined (→ "—"). NEVER ?? 0 — old snapshots predate
      // these columns and a fake 0 would lie.
      slg:             parseNum(snap.slg)             ?? s.batting.slg,
      hbp:             snap.hbp                       ?? s.batting.hbp,
      xbh:             snap.xbh                       ?? s.batting.xbh,
      qab:             snap.qab                       ?? s.batting.qab,
      hhb:             snap.hhb                       ?? s.batting.hhb,
      twoOutRbi:       snap.twoOutRbi                 ?? s.batting.twoOutRbi,
      lob:             snap.lob                       ?? s.batting.lob,
      ps:              snap.ps                        ?? s.batting.ps,
      kLooking:        snap.kLooking                  ?? s.batting.kLooking,
      twoStrikeBattle: snap.twoStrikeBattle           ?? s.batting.twoStrikeBattle,
      contactPct:      normalizeRate(snap.contactPct) ?? s.batting.contactPct,
      baRisp:          parseNum(snap.baRisp)          ?? s.batting.baRisp,
      ldPct:           normalizeRate(snap.ldPct)      ?? s.batting.ldPct,
      fbPct:           normalizeRate(snap.fbPct)      ?? s.batting.fbPct,
      gbPct:           normalizeRate(snap.gbPct)      ?? s.batting.gbPct,
      babipBat:        parseNum(snap.babipBat)        ?? s.batting.babipBat,
      qabPct: (snap.qab != null && (snap.pa ?? 0) > 0)
        ? snap.qab / snap.pa
        : (normalizeRate(snap.qabPct) ?? s.batting.qabPct),
      psPa: (snap.ps != null && (snap.pa ?? 0) > 0)
        ? snap.ps / snap.pa
        : (parseNum(snap.psPa) ?? s.batting.psPa),
      battlePct: (snap.twoStrikeBattle != null && (snap.pa ?? 0) > 0)
        ? snap.twoStrikeBattle / snap.pa
        : (normalizeRate(snap.battlePct) ?? s.batting.battlePct),
    };

    const snapIP = snap.ip ?? 0;
    if (snapIP > 0 || (snap.pitches ?? 0) > 0) {
      s.pitching = {
        ...s.pitching,
        gp:      snap.gp        ?? s.pitching.gp,
        ip:      snapIP,
        pitches: snap.pitches   ?? 0,
        so:      snap.soAllowed  ?? 0,
        bb:      snap.bbAllowed  ?? 0,
        h:       snap.hAllowed   ?? 0,
        er:      snap.erAllowed  ?? 0,
        hr:      snap.hrAllowed  ?? 0,
        hbp:     snap.hbpAllowed ?? 0,
        sbAllowed: snap.sbAllowed ?? 0,
        csAllowed: snap.csAllowed ?? 0,
        bf:      snap.bf         ?? 0,
        era:     snap.era        ?? 0,
        whip:    snap.whip       ?? 0,
        sPct:    snap.sPct       ?? s.pitching.sPct,
        fpsPct:  snap.fpsPct     ?? s.pitching.fpsPct,
        p_bf:    snap.p_bf       ?? s.pitching.p_bf,
        // Story stats — same snapshot-wins / never-fake-0 rule as batting
        ipTrue:      ipToInnings(snapIP) || s.pitching.ipTrue,
        rAllowed:    snap.rAllowed                   ?? s.pitching.rAllowed,
        zeroBbInn:   snap.zeroBbInn                  ?? s.pitching.zeroBbInn,
        bbs:         snap.bbs                        ?? s.pitching.bbs,
        lobb:        snap.lobb                       ?? s.pitching.lobb,
        loo:         snap.loo                        ?? s.pitching.loo,
        inn123:      snap.inn123                     ?? s.pitching.inn123,
        lowPitchInn: snap.lowPitchInn                ?? s.pitching.lowPitchInn,
        fpsoPct:     normalizeRate(snap.fpsoPct)     ?? s.pitching.fpsoPct,
        fpswPct:     normalizeRate(snap.fpswPct)     ?? s.pitching.fpswPct,
        fpshPct:     normalizeRate(snap.fpshPct)     ?? s.pitching.fpshPct,
        smPct:       normalizeRate(snap.smPct)       ?? s.pitching.smPct,
        kBf:         parseNum(snap.kBf)              ?? s.pitching.kBf,
        weakPct:     normalizeRate(snap.weakPct)     ?? s.pitching.weakPct,
        goAo:        parseNum(snap.goAo)             ?? s.pitching.goAo,
        baa:         parseNum(snap.baa)              ?? s.pitching.baa,
        pIp: (ipToInnings(snapIP) > 0 && (snap.pitches ?? 0) > 0)
          ? (snap.pitches ?? 0) / ipToInnings(snapIP)
          : (parseNum(snap.pIp) ?? s.pitching.pIp),
        bbInn: ipToInnings(snapIP) > 0
          ? (snap.bbAllowed ?? 0) / ipToInnings(snapIP)
          : s.pitching.bbInn,
      };
      // Recompute FIP/BABIP/DSS from the snapshot's counting stats when available.
      if ((snap.bf ?? 0) > 0 && snapIP > 0) {
        s.pitching.fip   = calculateFIP(s.pitching.so, s.pitching.bb, s.pitching.hbp, s.pitching.hr, snapIP);
        s.pitching.babip = calculateBABIP(s.pitching.h, s.pitching.hr, s.pitching.bf, s.pitching.bb, s.pitching.hbp, s.pitching.so);
        s.pitching.dss   = s.pitching.fip != null
          ? calculateDSS(s.pitching.era, s.pitching.fip, s.pitching.babip, snapIP)
          : null;
      }
    }
  });

  return { stats, lastGCOuting, totalGamesWithLogs };
}
